import React, { useState, useEffect } from 'react';
import { X, Loader2, Users, Calendar, Mail, Tag, Clock, Edit } from 'lucide-react';
import { User, Tenant } from '../../types';
import { getUser, getTenant } from '../../services/api';
import { formatDate } from '../../utils/format';
import Badge from '../ui/Badge';
import UserFormModal from './UserFormModal';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';

interface UserDetailModalProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit?: (userId: string) => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  userId,
  isOpen,
  onClose,
  onEdit
}) => {
  const { user: currentUser } = useAuth();
  const { canEditUsers } = usePermissions();
  const [user, setUser] = useState<User | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Add backdrop effect to body when modal opens
  useEffect(() => {
    if (isOpen) {
      // Add backdrop blur class to body
      document.body.classList.add('modal-open');
      // Disable scrolling on the body
      document.body.style.overflow = 'hidden';
    } else {
      // Remove backdrop blur class from body
      document.body.classList.remove('modal-open');
      // Re-enable scrolling
      document.body.style.overflow = '';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !userId) return;
    
    loadUserData();
  }, [userId, isOpen]);

  // Load user data function (extracted to be reusable)
  const loadUserData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const userData = await getUser(userId);
      setUser(userData);
      
      // Fetch tenant information if user has a tenant_id
      if (userData.tenant_id) {
        try {
          const tenantData = await getTenant(userData.tenant_id);
          setTenant(tenantData);
        } catch (err) {
          // We don't set an error here as the user data is still loaded
        }
      }
    } catch (err) {
      setError('Failed to load user details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle opening the edit modal
  const handleOpenEditModal = () => {
    // Check if user is editing themselves or has edit permission
    const isEditingSelf = userId === currentUser?.id;
    
    // Allow self-editing without permission check (but role changes will be restricted in the modal)
    // For editing others, require edit permission
    if (!isEditingSelf && !canEditUsers()) {
      return; // Don't open edit modal if no permission
    }
    
    setIsEditModalOpen(true);
  };

  // Handle closing the edit modal and refreshing data
  const handleEditModalClose = async () => {
    setIsEditModalOpen(false);
    // Reload user data to reflect changes
    await loadUserData();
  };

  // Get user's full name or username
  const getUserDisplayName = () => {
    if (!user) return '';
    
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    
    return user.username;
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-hidden">
        <div className="bg-background-card rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-modalOpen">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-dark sticky top-0 bg-background-card z-10">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-full bg-blue-600/20 border border-blue-600/30 text-blue-500 flex items-center justify-center mr-3">
                <Users size={20} />
              </div>
              <div className="max-w-[calc(100%-4rem)]">
                <h2 className="text-xl font-semibold">
                  {isLoading ? 'Loading User...' : getUserDisplayName()}
                </h2>
                {user && (
                  <p className="text-sm text-text-muted-dark truncate">
                    {user.username}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {user && (() => {
                const isEditingSelf = userId === currentUser?.id;
                const canEdit = isEditingSelf || canEditUsers();
                
                return canEdit ? (
                  <button 
                    onClick={handleOpenEditModal}
                    className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border border-white/20 px-3 py-1.5 rounded-lg flex items-center transition-all duration-200"
                  >
                    <Edit size={14} className="mr-1" />
                    Edit
                  </button>
                ) : null;
              })()}
              <button 
                onClick={onClose}
                className="p-2 rounded-full hover:bg-white/10 backdrop-blur-sm text-white hover:text-white transition-colors"
                aria-label="Close"
              >
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex justify-center items-center min-h-[200px]">
                <div className="text-center">
                  <Loader2 className="h-10 w-10 animate-spin text-primary mb-4 mx-auto" />
                  <p className="text-text-muted-dark">Loading user details...</p>
                </div>
              </div>
            ) : error ? (
              <div className="bg-status-danger/10 border border-status-danger/20 text-status-danger rounded-md p-4">
                <p>{error}</p>
              </div>
            ) : user ? (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                {/* Left column - Basic info */}
                <div className="md:col-span-2 space-y-6">
                  <div className="bg-background-hover/30 border border-border-dark rounded-lg p-4">
                    <h3 className="text-lg font-medium mb-4">User Information</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <Users className="w-5 h-5 text-text-muted-dark mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <div className="text-sm text-text-muted-dark">Username</div>
                          <div>{user.username}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Mail className="w-5 h-5 text-text-muted-dark mt-0.5 mr-3 flex-shrink-0" />
                        <div className="w-full max-w-[calc(100%-2rem)]">
                          <div className="text-sm text-text-muted-dark">Email</div>
                          <div className="break-all">{user.email}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Tag className="w-5 h-5 text-text-muted-dark mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <div className="text-sm text-text-muted-dark">Role</div>
                          <div className="flex items-center">
                            <Badge 
                              variant={
                                user.role?.toLowerCase() === 'admin' ? 'info' : 
                                user.role === 'manager' ? 'warning' : 
                                'default'
                              }
                            >
                              {user.role}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-start">
                        <Calendar className="w-5 h-5 text-text-muted-dark mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <div className="text-sm text-text-muted-dark">Joined</div>
                          <div>{formatDate(user.created_at)}</div>
                        </div>
                      </div>
                      
                      {user.last_login && (
                        <div className="flex items-start">
                          <Clock className="w-5 h-5 text-text-muted-dark mt-0.5 mr-3 flex-shrink-0" />
                          <div>
                            <div className="text-sm text-text-muted-dark">Last Login</div>
                            <div>{formatDate(user.last_login)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-background-hover/30 border border-border-dark rounded-lg p-4">
                    <h3 className="text-lg font-medium mb-4">Status</h3>
                    <div className="flex items-center">
                      <Badge 
                        variant={user.status === 'active' ? 'success' : 'danger'}
                        withDot={true}
                        size="lg"
                      >
                        {user.status === 'active' ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                {/* Right column - Additional info */}
                <div className="md:col-span-3">
                  <div className="bg-background-hover/30 border border-border-dark rounded-lg p-4">
                    <h3 className="text-lg font-medium mb-4">Personal Information</h3>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <div className="text-sm text-text-muted-dark">Full Name</div>
                        <div>{user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}` 
                          : 'Not provided'}
                        </div>
                      </div>
                      
                      {tenant && (
                        <div className="mb-6">
                          <div className="text-sm text-text-muted-dark mb-1">Tenant</div>
                          <div className="flex items-center">
                            <Badge 
                              variant="default"
                              size="lg"
                            >
                              {tenant.name} <span className="text-xs ml-1 opacity-70">({tenant.level})</span>
                            </Badge>
                          </div>
                          
                          {/* Display tenant information */}
                          <div className="mt-4 grid grid-cols-1 gap-2">
                            {tenant.email_to && (
                              <div>
                                <div className="text-sm text-text-muted-dark">Email</div>
                                <div className="break-all max-w-full truncate">{tenant.email_to}</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Add more sections here as needed */}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p>No user information found.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit User Modal - only render when actually opening edit */}
      {user && isEditModalOpen && (
        <UserFormModal
          userId={userId}
          isOpen={isEditModalOpen}
          onClose={handleEditModalClose}
          onSaved={loadUserData}
        />
      )}
    </>
  );
};

export default UserDetailModal; 